import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Frames out of a video the person just picked — for the COVER strip and the cover still.
 * Design handoff: docs/plans/2026-09-portals-stories/handoff-composer/README.md §02.
 *
 * Everything here reads the LOCAL file through its object URL. Nothing is uploaded while
 * composing, by rule: the file goes up when they press SEND and not a moment before.
 *
 * The decoding trick (from the prototype): the scratch <video> has to be IN the document for
 * the browser to actually decode on seek — an entirely detached element hands back blank
 * frames — so it lives in a 1px corner of the viewport and is cleaned up on unmount.
 *
 * **Two stages, and only the first one is allowed to block the composer** (iOS, Oct 2026).
 * WebKit will give a second video element metadata but often not a decoder while the preview
 * holds one, so `loadeddata` never fires and `play()` is refused. Waiting on either of those
 * left TRIM + COVER on "Loading video…" for ever with no way past it: a video Portal could not
 * be made on an iPhone at all. So:
 *
 *   1. DURATION — `loadedmetadata` (readyState 1, no decoder needed), on a timeout. This is
 *      everything the trim window needs, and publishing it is what opens the gate.
 *   2. FRAMES — best effort, on a budget, and only while the element really has a frame to
 *      give (`readyState >= 2`). No frames means a plain grey strip, which trims exactly the
 *      same; the cover still is then the one thing missing, and the server makes its own from
 *      `coverMs` anyway.
 *
 * Nothing here may hang: every wait is raced against a timer.
 */
const STRIP_FRAMES = 8;
/** A long file (a 15-minute video message) gets a denser strip, or a frame stands for two minutes. */
export function stripFramesFor(durationSec) {
  if (!(durationSec > 0)) return STRIP_FRAMES;
  if (durationSec > 600) return 24;
  if (durationSec > 120) return 16;
  return STRIP_FRAMES;
}

/** How long to wait for the file to say how long it is before giving up on it. */
const META_TIMEOUT_MS = 8000;
/** The whole strip's budget. A seek that never lands must not hold the step. */
const FRAMES_BUDGET_MS = 7000;

/** Resolves on the first of `events`, or `null` on timeout; rejects on the element's error. */
function waitFor(el, events, ms) {
  return new Promise((resolve, reject) => {
    let timer = 0;
    const clean = () => {
      clearTimeout(timer);
      events.forEach((e) => el.removeEventListener(e, hit));
      el.removeEventListener('error', failed);
    };
    const hit = (e) => { clean(); resolve(e?.type || true); };
    const failed = () => { clean(); reject(new Error('media error')); };
    timer = setTimeout(() => { clean(); resolve(null); }, ms);
    events.forEach((e) => el.addEventListener(e, hit, { once: true }));
    el.addEventListener('error', failed, { once: true });
  });
}

function seek(video, time) {
  return new Promise((resolve) => {
    if (!Number.isFinite(time)) { resolve(); return; }
    let timer = 0;
    const done = () => {
      clearTimeout(timer);
      video.removeEventListener('seeked', done);
      resolve();
    };
    // A seek that never lands must not take the strip down with it.
    timer = setTimeout(done, 1500);
    video.addEventListener('seeked', done);
    try { video.currentTime = time; } catch { done(); }
  });
}

/**
 * How long the clip is, for files that won't say.
 *
 * Plenty of real video arrives without a duration in its metadata — anything recorded rather
 * than encoded, streamed webm, some phone captures — and reports Infinity until it has been
 * played through. Seeking past the end forces the browser to work it out. Without this the
 * frame times are all NaN, every seek hangs, and the cover step sits empty with nothing to say.
 */
async function resolveDuration(video) {
  if (Number.isFinite(video.duration) && video.duration > 0) return video.duration;
  await new Promise((resolve) => {
    let timer = 0;
    const settle = () => {
      clearTimeout(timer);
      video.removeEventListener('durationchange', settle);
      resolve();
    };
    timer = setTimeout(settle, 2000);
    video.addEventListener('durationchange', settle);
    try { video.currentTime = 1e101; } catch { settle(); }
  });
  try { video.currentTime = 0; } catch { /* it will start where it starts */ }
  return Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
}

function drawCover(ctx, video, w, h) {
  const vw = video.videoWidth || w;
  const vh = video.videoHeight || h;
  const ratio = w / h;
  let sw; let sh; let sx; let sy;
  if (vw / vh > ratio) { sh = vh; sw = sh * ratio; sx = (vw - sw) / 2; sy = 0; }
  else { sw = vw; sh = sw / ratio; sx = 0; sy = (vh - sh) / 2; }
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, w, h);
}

/** HAVE_CURRENT_DATA: there is a frame on the element to draw. Below this a canvas gets black. */
const hasFrame = (v) => v && v.readyState >= 2 && v.videoWidth > 0;

/**
 * The one element in the document that DOES have a decoded frame of this file. On iOS that is
 * usually the composer's own preview — which the COVER tab has already parked on exactly the
 * frame being chosen (PortalPreview's `seeking`), so its current frame IS the cover. Read, never
 * seeked: the preview belongs to the person watching it.
 */
function frameSource(scratchEl) {
  if (hasFrame(scratchEl)) return scratchEl;
  return sibling(scratchEl, hasFrame);
}

/** Another element in the document playing this same file, that `ok` is happy with. */
function sibling(scratchEl, ok) {
  const src = scratchEl?.currentSrc || scratchEl?.src;
  if (!src) return null;
  const all = document.querySelectorAll('video');
  for (let i = 0; i < all.length; i += 1) {
    const el = all[i];
    if (el !== scratchEl && (el.currentSrc === src || el.src === src) && ok(el)) return el;
  }
  return null;
}

/** A length off the preview, for a scratch element the engine never got round to. */
function borrowedDuration(scratchEl) {
  const el = sibling(scratchEl, (v) => Number.isFinite(v.duration) && v.duration > 0);
  return el ? el.duration : 0;
}

export default function useVideoFrames(localUrl, kind, clip = null) {
  const [frames, setFrames] = useState([]);
  const [durationSec, setDurationSec] = useState(0);
  const [cover, setCover] = useState(null);       // data URL of the chosen frame
  const [extracting, setExtracting] = useState(false);
  const scratch = useRef(null);
  const busy = useRef(false);
  const coverTimer = useRef(null);

  // Tear down the scratch element and any pending capture when the file changes or we leave.
  const release = useCallback(() => {
    if (coverTimer.current) clearTimeout(coverTimer.current);
    const v = scratch.current;
    if (v?.parentNode) v.parentNode.removeChild(v);
    scratch.current = null;
    busy.current = false;
  }, []);

  useEffect(() => release, [release]);

  useEffect(() => {
    if (kind !== 'video' || !localUrl) {
      release();
      setFrames([]); setDurationSec(0); setCover(null);
      return undefined;
    }
    let cancelled = false;
    release();                       // the last file's element and any capture still pending
    setExtracting(true);
    setFrames([]); setDurationSec(0); setCover(null);
    const v = document.createElement('video');
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.preload = 'auto';
    // In the viewport, not off in the margins: WebKit is readier to decode an element it
    // considers on screen. One pixel in the corner, under everything, inert to touch.
    v.style.cssText = 'position:fixed;left:0;bottom:0;width:1px;height:1px;opacity:0.01;z-index:-1;pointer-events:none;';
    document.body.appendChild(v);
    scratch.current = v;
    v.src = localUrl;
    try { v.load(); } catch { /* it is loading already */ }

    (async () => {
      try {
        // ── 1 · how long is it ──────────────────────────────────────────────────────────
        await waitFor(v, ['loadedmetadata', 'loadeddata', 'canplay'], META_TIMEOUT_MS);
        if (cancelled) return;
        let d = await resolveDuration(v);
        // Nothing from our own element — borrow the length off the composer's preview, which is
        // showing this very file. One iPhone's refusal to look at a second video must not be the
        // end of making a Portal.
        if (!d) d = borrowedDuration(v);
        if (cancelled) return;
        setDurationSec(d);                   // the gate opens here, frames or no frames
        if (!d) return;

        // ── 2 · the strip, as far as it gets ────────────────────────────────────────────
        // A play/pause cycle gets the first frame decoded on browsers that won't seek cold.
        // iOS refuses it while something else holds the decoder; that is not an error.
        try { await v.play(); v.pause(); } catch { /* autoplay policy, or no decoder spare */ }
        if (cancelled) return;
        const total = stripFramesFor(d);
        await seek(v, (d * 0.5) / total);
        if (cancelled || !hasFrame(v)) return;      // no decoder for us: grey strip, still trims

        const canvas = document.createElement('canvas');
        canvas.width = 90; canvas.height = 160;
        const ctx = canvas.getContext('2d');
        const until = Date.now() + FRAMES_BUDGET_MS;
        const out = [];
        for (let i = 0; i < total; i += 1) {
          if (cancelled || Date.now() > until) break;
          // eslint-disable-next-line no-await-in-loop
          if (i > 0) await seek(v, (d * (i + 0.5)) / total);
          if (cancelled) break;
          drawCover(ctx, v, 90, 160);
          out.push(canvas.toDataURL('image/jpeg', 0.7));
        }
        if (cancelled || out.length === 0) return;
        // Pad a budget-cut strip so the grid keeps its shape and the missing ones read as gaps.
        while (out.length < total) out.push(null);
        setFrames(out);
      } catch {
        // A file the browser cannot read at all. `durationSec` stays 0 and the step says so.
        if (!cancelled) setFrames([]);
      } finally {
        if (!cancelled) setExtracting(false);
      }
    })();

    return () => {
      cancelled = true;
      if (scratch.current === v && v.parentNode) { v.parentNode.removeChild(v); scratch.current = null; }
    };
  }, [localUrl, kind, release]);

  /** The still at `pct` (0–1) through the CLIP — the ring face and the share-card frame. */
  const captureCover = useCallback((pct) => {
    if (coverTimer.current) clearTimeout(coverTimer.current);
    coverTimer.current = setTimeout(async () => {
      const v = scratch.current;
      if (!v || busy.current) return;
      busy.current = true;
      try {
        const full = durationSec || (Number.isFinite(v.duration) ? v.duration : 0);
        // Inside the trimmed window when there is one — the cover has to be a frame the
        // Portal actually contains.
        const from = clip && clip.endMs > clip.startMs ? clip.startMs / 1000 : 0;
        const span = clip && clip.endMs > clip.startMs ? (clip.endMs - clip.startMs) / 1000 : full;
        if (hasFrame(v) && span > 0) await seek(v, from + pct * span);
        // Nothing decoded here (iOS, with the preview holding the decoder): borrow the frame
        // off whatever element does have one, and if there is none, no still rather than a
        // black one — the server cuts its own thumbnail at `coverMs`.
        const donor = frameSource(v);
        if (!donor) { busy.current = false; return; }
        const canvas = document.createElement('canvas');
        canvas.width = 180; canvas.height = 320;
        drawCover(canvas.getContext('2d'), donor, 180, 320);
        setCover(canvas.toDataURL('image/jpeg', 0.8));
      } catch { /* a missing still is a worse ring, not a broken Portal */ }
      busy.current = false;
    }, 120);
  }, [durationSec, clip]);

  return { frames, durationSec, cover, setCover, captureCover, extracting };
}
